/* eslint-disable no-template-curly-in-string */
import { memo, useEffect, useMemo, useState } from "react";

import { useCart } from "@shopify/hydrogen-react";
import { type CartLine } from "@shopify/hydrogen-react/storefront-api-types";

import {
  type CartTieredProgressBarContent,
  type CartTieredProgressBarSettings,
} from "@tablescape/state";
import {
  formatMoney,
  getTotalWithoutBMSMValue,
  hasBundlesLineItemAttribute,
  hasTotalWithoutBMSMLineItemAttribute,
} from "@tablescape/utilities";

import { CartTieredProgressBarView } from "./CartTieredProgressBar.view";

type Props = CartTieredProgressBarContent &
  CartTieredProgressBarSettings & {
    progressBarVisible: boolean;
    totalAmount?: number;
  };

type NonNullableThreshold = {
  thresholdAmount: number;
  thresholdInProgressSubtitleText: string;
  thresholdItemText: string;
  thresholdReachedTitleText: string;
};

const CartTieredProgressBarBase = ({
  colorGradient,
  finalSubtitleText,
  iconName,
  initialTitleText,
  progressBarVisible,
  thresholds: originalThresholds,
  totalAmount,
}: Props) => {
  const { lines } = useCart();
  const [progressPercentage, setProgressPercentage] = useState(0);

  const totalAmountInCart = useMemo(() => {
    if (totalAmount) {
      return totalAmount;
    }

    const linesTotal = lines
      .filter((line): line is CartLine => !!line?.cost)
      .map((line) => {
        if (
          hasBundlesLineItemAttribute(line) &&
          hasTotalWithoutBMSMLineItemAttribute(line)
        ) {
          return getTotalWithoutBMSMValue(line);
        }

        return parseFloat(line.cost.subtotalAmount.amount);
      })
      .reduce((a, b) => a + b, 0);

    return linesTotal ?? 0;
  }, [lines, totalAmount]);

  const thresholds = useMemo(
    () =>
      originalThresholds.filter(
        (
          threshold: CartTieredProgressBarContent["thresholds"][number]
        ): threshold is NonNullableThreshold => {
          return (
            typeof threshold.thresholdAmount === "number" &&
            !!threshold.thresholdInProgressSubtitleText &&
            typeof threshold.thresholdInProgressSubtitleText === "string" &&
            typeof threshold.thresholdItemText === "string" &&
            !!threshold.thresholdReachedTitleText &&
            typeof threshold.thresholdReachedTitleText === "string"
          );
        }
      ),
    [originalThresholds]
  );

  const maxThresholdValue = useMemo(() => {
    return Math.max(
      ...thresholds.map((threshold) => threshold.thresholdAmount)
    );
  }, [thresholds]);

  const thresholdItems = useMemo(
    () =>
      thresholds.map(
        (
          {
            thresholdAmount,
            thresholdInProgressSubtitleText,
            thresholdItemText,
            thresholdReachedTitleText,
          },
          index
        ) => {
          const cappedTotalAmount = Math.min(
            totalAmountInCart,
            maxThresholdValue
          );

          const isFirstThresholdItem = index === 0;
          const isLastThresholdItem = index === thresholds.length - 1;
          const hasReachedThreshold = cappedTotalAmount >= thresholdAmount;
          const hasReachedPreviousThreshold =
            cappedTotalAmount >= thresholds[index - 1]?.thresholdAmount;
          const isTotalAbovePreviousThreshold =
            cappedTotalAmount >= thresholds[index - 1]?.thresholdAmount;
          const isTotalBelowPreviousThreshold =
            cappedTotalAmount <= thresholds[index - 1]?.thresholdAmount;
          const isTotalAboveCurrentThreshold =
            cappedTotalAmount >= thresholdAmount;
          const isTotalBelowCurrentThreshold =
            cappedTotalAmount < thresholdAmount;
          const isTotalBelowNextThreshold =
            cappedTotalAmount <= thresholds[index + 1]?.thresholdAmount;
          const isTotalAboveNextThreshold =
            cappedTotalAmount >= thresholds[index + 1]?.thresholdAmount;
          const isTotalAboveLastPreviousThreshold =
            cappedTotalAmount > thresholds[index - 2]?.thresholdAmount;

          const isCurrentThreshold =
            (isTotalBelowCurrentThreshold || isLastThresholdItem) &&
            (hasReachedPreviousThreshold || isFirstThresholdItem);
          const isPreviousThreshold =
            isTotalAboveCurrentThreshold &&
            !isLastThresholdItem &&
            (isTotalAbovePreviousThreshold || isFirstThresholdItem) &&
            !isTotalAboveNextThreshold;
          const isNextThreshold =
            isTotalBelowPreviousThreshold &&
            !isFirstThresholdItem &&
            (isTotalBelowNextThreshold || isLastThresholdItem) &&
            !isCurrentThreshold &&
            (index === 1 || isTotalAboveLastPreviousThreshold);

          return {
            amount: thresholdAmount,
            hasReachedThreshold,
            index,
            isCurrentThreshold,
            isNextThreshold,
            isPreviousThreshold,
            subtitleText: thresholdInProgressSubtitleText,
            text: thresholdItemText,
            titleText: thresholdReachedTitleText,
          };
        }
      ),
    [maxThresholdValue, thresholds, totalAmountInCart]
  );

  const previousThreshold = useMemo(
    () => thresholdItems.find(({ isPreviousThreshold }) => isPreviousThreshold),
    [thresholdItems]
  );

  const currentThreshold = useMemo(() => {
    const currentOrFirstThreshold =
      thresholdItems.find(({ isCurrentThreshold }) => isCurrentThreshold) ??
      thresholdItems[0];

    return {
      ...currentOrFirstThreshold,
    };
  }, [thresholdItems]);

  const thresholdValues = useMemo(() => {
    const increment = 1 / thresholdItems.length;

    const fulfilledThreshold =
      thresholdItems.find(({ amount }) => amount === totalAmountInCart) ??
      currentThreshold;

    return {
      percentageFulfilled: Math.min(
        totalAmountInCart / (fulfilledThreshold.amount ?? 0),
        1
      ),
      increment,
      percentageOffset: increment * (fulfilledThreshold.index ?? 0),
    };
  }, [currentThreshold, thresholdItems, totalAmountInCart]);

  const currentAmount = useMemo(
    () => maxThresholdValue - totalAmountInCart,
    [maxThresholdValue, totalAmountInCart]
  );

  useEffect(() => {
    if (!thresholdValues || !progressBarVisible) {
      setProgressPercentage(0);
      return;
    }

    requestAnimationFrame(() => {
      setProgressPercentage(
        (thresholdValues.percentageFulfilled * thresholdValues.increment +
          thresholdValues.percentageOffset) *
          100
      );
    });
  }, [thresholdValues, progressBarVisible]);

  const currencySuffix = useMemo(() => {
    switch (window.Shopify?.currency?.active) {
      case "AUD":
      case "CAD": {
        return window.Shopify.currency.active;
      }

      default: {
        return undefined;
      }
    }
  }, []);

  const hasReachedInitialThreshold = useMemo(() => {
    return totalAmountInCart >= thresholds[0]?.thresholdAmount;
  }, [thresholds, totalAmountInCart]);

  const hasReachedMaxThreshold = useMemo(() => {
    return totalAmountInCart >= maxThresholdValue;
  }, [maxThresholdValue, totalAmountInCart]);

  const titleText = useMemo(() => {
    if (hasReachedMaxThreshold) {
      return currentThreshold.titleText;
    }

    if (hasReachedInitialThreshold) {
      return previousThreshold?.titleText;
    }

    return initialTitleText;
  }, [
    currentThreshold.titleText,
    hasReachedInitialThreshold,
    hasReachedMaxThreshold,
    initialTitleText,
    previousThreshold?.titleText,
  ]);

  const progressText = useMemo(() => {
    if (currentAmount > 0) {
      return currentThreshold.subtitleText.replace(
        "${x}",
        formatMoney(
          Math.round(currentThreshold.amount - totalAmountInCart) * 100,
          "${{ amount_no_trailing_zeroes }}",
          currencySuffix
        )
      );
    }

    return finalSubtitleText;
  }, [
    currencySuffix,
    currentAmount,
    finalSubtitleText,
    currentThreshold.amount,
    currentThreshold.subtitleText,
    totalAmountInCart,
  ]);

  return thresholds.length ? (
    <CartTieredProgressBarView
      colorGradient={colorGradient}
      hasReachedInitialThreshold={hasReachedInitialThreshold}
      iconName={iconName}
      progressPercentage={progressPercentage}
      progressText={progressText}
      thresholdItems={thresholdItems}
      titleText={titleText}
    />
  ) : null;
};

export const CartTieredProgressBar = memo(CartTieredProgressBarBase);
